import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const trimTrailingSlash = (value) => (value.endsWith('/') ? value.slice(0, -1) : value);
const decodeTabPath = (value) =>
  (value || '')
    .split('/')
    .filter(Boolean)
    .map((segment) => decodeURIComponent(segment))
    .join('/');
const encodeTabPath = (value) =>
  (value || '')
    .split('/')
    .filter(Boolean)
    .map((segment) => encodeURIComponent(segment))
    .join('/');

export const useDashboardTabRoute = (basePath, defaultTab) => {
  const navigate = useNavigate();
  const location = useLocation();
  const normalizedBase = useMemo(() => trimTrailingSlash(basePath), [basePath]);

  const getTabFromPathname = useCallback(
    (pathname) => {
      const path = trimTrailingSlash(pathname);
      if (path === normalizedBase) return defaultTab;
      const prefix = `${normalizedBase}/`;
      if (path.startsWith(prefix)) {
        const raw = path.slice(prefix.length);
        return raw ? decodeTabPath(raw) : defaultTab;
      }
      return defaultTab;
    },
    [defaultTab, normalizedBase]
  );

  const [activeTab, setActiveTabState] = useState(() => getTabFromPathname(location.pathname));

  useEffect(() => {
    setActiveTabState(getTabFromPathname(location.pathname));
  }, [location.pathname, getTabFromPathname]);

  const setActiveTab = useCallback(
    (tab) => {
      const targetTab = tab || defaultTab;
      const targetPath =
        targetTab === defaultTab
          ? normalizedBase
          : `${normalizedBase}/${encodeTabPath(targetTab)}`;
      setActiveTabState(targetTab);
      if (location.pathname !== targetPath) {
        navigate(targetPath);
      }
    },
    [defaultTab, location.pathname, navigate, normalizedBase]
  );

  return [activeTab, setActiveTab];
};
